import React from "react";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import { Button, Card } from "monica-alexandria";
import { Sales_Lead_Card } from "./Sales_Lead_Card";
import { prospectLeadColumnTemplate, read_is_backlog_lead, read_lead_assignee, read_member_keys } from "../utils/sales_helpers";

const ProspectLeadsColumns = styled.div`
    display: grid;
    grid-template-columns: ${p => p.columnsValue};
    gap: 1.2rem;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-x: auto;
    padding-bottom: 1.5rem;
    transition: grid-template-columns 0.4s ease;
`;

const inProgressColumnFlash = keyframes`
    0% {
        border-color: var(--error);
        background: color-mix(in srgb, var(--error) 12%, transparent);
    }

    50% {
        border-color: var(--warning);
        background: color-mix(in srgb, var(--warning) 14%, transparent);
    }

    100% {
        border-color: var(--error);
        background: color-mix(in srgb, var(--error) 12%, transparent);
    }
`;

const ProspectLeadColumn = styled(Card)`
    height: calc(100vh - 31rem);
    min-width: 32rem;
    padding: 1.2rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    border: 0.2rem solid ${p => p.attention ? "var(--error)" : p.theme.mid};
    overflow: auto;
    animation: ${p => p.attention ? inProgressColumnFlash : "none"} 1.2s ease-in-out infinite;
`;

const ProspectLeadColumnHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.8rem;
`;

const ProspectLeadColumnButton = styled(Button)`
    background: transparent;
    padding: var(--smallPads);
    border-radius: var(--smallRadius);
    white-space: nowrap;

    h4 {
        white-space: nowrap;
    }
`;

const ProspectLeadColumnCount = styled.div`
    padding: 0.4rem 0.8rem;
    border-radius: var(--smallRadius);
    background: ${p => p.theme.low};
`;

const ProspectLeadColumnBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-height: 12rem;
`;

/**
 * Reads the stable React key for one saved Prospect brand card.
 */
const read_sales_card_key = (lead) => {
    return `${lead?.brandId || lead?._id || lead?.id || ""}`.trim();
};

/**
 * Renders the fixed Prospect Leads kanban board columns and draggable cards.
 */
export const Sales_Board = (props) => {
    const { t } = useTranslation();

    return (
        <ProspectLeadsColumns columnsValue={prospectLeadColumnTemplate}>
            {props?.boardColumns?.map((column) => (
                <ProspectLeadColumn
                    key={column.value}
                    attention={props?.attentionColumnStatus === column.value}
                    onDrop={(event) => props?.onDrop(event, column.value)}
                    onDragOver={props?.onDragOver}
                >
                    <ProspectLeadColumnHeader>
                        <ProspectLeadColumnButton>
                            <h4>{t(column.label)}</h4>
                        </ProspectLeadColumnButton>
                        <ProspectLeadColumnCount>
                            <h6>{column.leads.length}</h6>
                        </ProspectLeadColumnCount>
                    </ProspectLeadColumnHeader>
                    <ProspectLeadColumnBody>
                        {column.leads.map((lead) => (
                            <Sales_Lead_Card
                                key={read_sales_card_key(lead)}
                                lead={lead}
                                teamMembers={props?.teamMembers}
                                dragLeadId={props?.dragLeadId}
                                dragDisabled={read_is_backlog_lead(lead)}
                                dragAllowed={!read_is_backlog_lead(lead)}
                                onDragStart={props?.onDragStart}
                                onDragEnd={props?.onDragEnd}
                                onOpenLead={props?.onOpenLead}
                                canClaim={props?.canClaim && read_member_keys(read_lead_assignee(lead)).length === 0}
                                claiming={props?.claimingLeadId === read_sales_card_key(lead)}
                                onClaim={props?.onClaim}
                            />
                        ))}
                    </ProspectLeadColumnBody>
                </ProspectLeadColumn>
            ))}
        </ProspectLeadsColumns>
    );
};
